import { ReactNode } from 'react';
import { Assets, AssetsClass, DisplayObject, FederatedEventTarget } from 'pixi.js';
import { Animation, AnimationStateData, TrackEntry } from '@pixi-spine/runtime-4.1';
import { IAnimation, IAnimationStateListener, ITimeline, ITrackEntry, Spine as pixiSpine } from 'pixi-spine';

export class Spine extends pixiSpine {
  animationPromises: any | Map<unknown, unknown> = null;
  key: string = '';
  _listeners: any[] = [];
  PixiAssets: any | AssetsClass = null;
  roundPixels: boolean = false;
  stateData: any | AnimationStateData = null
  constructor({ key, animation }: { key: string, animation?: any }) {
    const { spineData } = Assets.get(key);

    super(spineData);
    this.animationPromises = new Map();
    this.key = key;
    this.PixiAssets = Assets;
    this._listeners = [];
    this.roundPixels = false;
    this.show = () => {
      this.visible = true;
      this.lastTime = 0;
    };
    if (animation) {
      this.play(animation.name, animation.loop, animation.timeScale);
    };
    this.addNamesOnSlots()
  };
  addNamesOnSlots(): void {
    const slots = this.skeleton?.slots;
    if (!slots) return;

    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i] as any;
      if (slot.data?.name && !slot.name) {
        slot.name = slot.data.name;
      }
    }
  }
  show(): void { };

  play = (animationName: string, loop = false, timeScale = 1, trackPlace = 0, reverse = false): void => {
    this.clearAnimationData(trackPlace);
    this.lastTime = 0;
    if (this.state?.hasAnimation(animationName)) {
      this.state.setAnimation(trackPlace, animationName, loop);
      this.state.tracks[trackPlace].timeScale = timeScale;
      if (reverse) this.reverseCurrentAnimation(this.state.tracks[trackPlace]);
    };
  };

  playAndWait = (
    alpha = 1,
    timeScale = 1,
    trackPlace = 0,
    reverse = false,
    event = 'complete',
    forceResolve = false,
    animationName: string,
  ): Promise<void> => {
    this.clearAnimationData(trackPlace);
    return new Promise<void>((r) => {
      this.play(animationName, false, timeScale, trackPlace, reverse);
      if (this.state.tracks[trackPlace]) this.state.tracks[trackPlace].alpha = alpha;
      if (this.getCurrentAnimationName(trackPlace) === animationName) {
        const o = (trackEntry: any) => {
          if (reverse && trackEntry.animationLast < trackEntry.trackTime) return;
          this.animationPromises.delete(trackPlace);
          r();
        };
        //@ts-ignore
        this.addListener(event, o, null, true, animationName);
        this.animationPromises.set(trackPlace, {
          forceResolve,
          resolver: r,
          animationName,
          event,
          listener: o,
        });
      } else r();
    });
  };

  clearAnimationData = (trackPlace: number): void => {
    if (this.animationPromises.has(trackPlace)) {
      const data: any = this.animationPromises.get(trackPlace);
      if (data.forceResolve) data.resolver();
      this.removeListener(data.event, data.listener, null, data.animationName);
      this.animationPromises.delete(trackPlace);
    };
  };

  reverseCurrentAnimation = (track: any | ITrackEntry, trackPlace?: number, name?: string): void => {
    if (!track) {
      if (trackPlace !== undefined) track = this.state.tracks[trackPlace];
      else track = this.state.tracks.find((a: any) => a.animation.name === name);
    };
    if (track) {
      if (
        //@ts-ignore
        track.timeScale > 0 && (track.trackTime > track.animationEnd || track.trackTime === track.animationStart)
      )
        track.trackTime = track.animationEnd;
      //@ts-ignore
      else if (track.timeScale < 0 && track.trackTime < track.animationStart)
        //@ts-ignore
        track.trackTime = track.animationStart;
      track.timeScale *= -1;
    };
  };

  accelerateCurrentAnimation = (factor: number): void => {
    const animation: ITrackEntry = this.state.tracks[0];
    if (animation) {
      animation.timeScale *= factor;
    };
  };

  stop = ({ trackPlace, animationName }: any | { trackPlace?: number, animationName?: string } = {}): void => {
    if (trackPlace === undefined && animationName)
      trackPlace = this.state.tracks.findIndex((t: any | TrackEntry) => t && t.animation.name === animationName);
    if (trackPlace >= 0) {
      this.clearTrack(trackPlace);
      if (this.animationPromises.has(trackPlace)) {
        const data: any = this.animationPromises.get(trackPlace);
        if (data.forceResolve) data.resolver();
        this.animationPromises.delete(trackPlace);
      };
    } else {
      this.state.clearTracks();
      this.skeleton.setToSetupPose();
      this.animationPromises.forEach((data: any) => {
        if (data.forceResolve) data.resolver();
      });
      this.animationPromises.clear();
    };
  };

  clearTrack = (index: number): void => {
    this.state.clearTrack(index);
  };

  stopAnimation = (name: string): void => {
    const index: number = this.state.tracks.findIndex((t: any | TrackEntry) => t && t.animation.name === name)
    if (index > -1) {
      this.state.clearTrack(index);
      this.state.tracks.splice(index, 1);
    };
  };

  getCurrentAnimationsData = (): ITrackEntry[] => {
    return this.state.tracks.reduce((r: ITrackEntry[], c: ITrackEntry, i: number) => {
      if (c) {
        r.push({
          //@ts-ignore
          name: c.animation.name,
          loop: c.loop,
          trackPlace: i,
          timeScale: c.timeScale,
        });
      };
      return r;
    }, []);
  };

  getCurrentAnimationName = (trackPlace = 0): string => {
    //@ts-ignore
    return this.state.tracks[trackPlace] ? this.state.tracks[trackPlace].animation.name : '';
  };

  getCurrentAnimationLoop = (): boolean => {
    return this.state.tracks[0] ? this.state.tracks[0].loop : false;
  };

  addOnCompleteHandler = (handler: Function, context: any = null, once = false): { [key: string]: Function } => {
    const listener: { [key: string]: Function } = {};
    function delayWrapper(...args: any) {
      requestAnimationFrame(() => {
        handler.call(context, ...args);
      });
    };
    let h: Function = delayWrapper;
    if (once) {
      h = () => {
        delayWrapper();
        this.state.removeListener(listener);
      };
    };
    listener.complete = h;
    this.state.addListener(listener);
    return listener;
  };

  removeOnCompleteHandler = (listener: IAnimationStateListener): void => {
    this.state.removeListener(listener);
  };

  isPlaying = (): boolean => {
    const animation: ITrackEntry = this.state.tracks[0];
    return !!(
      animation &&
      //@ts-ignore
      ((animation.timeScale > 0 && animation.getAnimationTime() < animation.animationEnd) || (animation.timeScale < 0 && animation.getAnimationTime() > 0))
    );
  };

  setSmoothTransition = (animationNames: string[], duration: number): void => {
    if (animationNames.length < 2 || duration === 0) return;
    for (const fromIndex in animationNames) {
      for (const toIndex in animationNames) {
        if (toIndex !== fromIndex)
          this.stateData.setMix(animationNames[fromIndex], animationNames[toIndex], duration);
      };
    };
  };

  getAnimationDuration = (animationName: string): number => {
    const animationData: any | IAnimation<ITimeline> = this.spineData.animations.find((a) => a.name === animationName);
    return animationData ? animationData.duration : 0;
  };

  // getTrackByAnimationName = (animationName: string): ITrackEntry | null => {
  //   return animationName
  //     ? this.state.tracks.find((a: any | TrackEntry) => a && a.animation.name === animationName)
  //     : null;
  // };

  //@ts-ignore
  addListener = (event: string, listener: Function, context: any, animationName: string, once = false): Function => {
    const isStandardEvent: boolean = ['start', 'complete', 'end', 'dispose', 'interrupted'].indexOf(event) > -1;
    const onceListener: Function = once
      ? (...args: any) => {
        this.removeListener(event, listener, context, animationName);
        listener.call(context, ...args);
      }
      : listener;
    const f: Function = isStandardEvent
      ? onceListener.bind(context)
      : (track: any, evt: any) => {
        if (evt.data.name === event) {
          onceListener.call(context, track, evt);
        };
      };
    const eventKey: string = isStandardEvent ? event : 'event';
    const l: { [key: string]: Function } = {
      [eventKey]: f,
    };
    // const track: any | ITrackEntry = this.getTrackByAnimationName(animationName);
    // if (track) {
    //   if (track.listener) {
    //     track.listener[eventKey] = f;
    //   } else track.listener = l;
    // } else this.state.addListener(l);
    this.state.addListener(l);
    this._listeners.push({ l, listener, context });
    return this.removeListener.bind(this, event, listener, context, animationName);
  };

  //@ts-ignore
  removeListener = (event: string, listener: Function, context: any, animationName: string): void => {
    const i: number = this._listeners.findIndex((a) => a.listener === listener && a.context === context);
    if (i > -1) {
      const data = this._listeners.splice(i, 1)[0];
      // if (animationName) {
      //   const track: any | ITrackEntry = this.getTrackByAnimationName(animationName);
      //   if (track && track.listener) {
      //     delete track.listener[event];
      //     if (Object.keys(track.listener).length === 0) track.listener = null;
      //   };
      // };
      this.state.removeListener(data.l);
    };
  };

  clearListeners = (): void => {
    this._listeners.length = 0;
    this.state.clearListeners();
    // this.state.tracks.forEach((t) => {
    //   if (t) t.listener = null;
    // });
  };

  callAllListenersAndStop = (): void => {
    this._listeners.forEach((a) => a.listener.call(a.context));
    this.stop();
    this.clearListeners();
  };

  addChildToSlot = (slotName: string, child: ReactNode, index = 0): boolean => {
    const p: DisplayObject = this.getSlot(slotName);
    if (p) {
      //@ts-ignore
      (p.children[index] || p).addChild(child);
      return true;
    };
    return false;
  };

  removeChildFromSlot = (slotName: string, child: ReactNode, index = 0): boolean => {
    const p: DisplayObject = this.getSlot(slotName);
    if (p) {
      //@ts-ignore
      (p.children[index] || p).removeChild(child);
      return true;
    };
    return false;
  };

  getSlot = (slotName: string): DisplayObject => {
    const index: number = this.spineData.slots.findIndex((slotData) => slotData.name === slotName);
    // Use slotContainers (indexed by setup order) rather than this.children.
    // During each render frame pixi-spine rewrites this.children[i] by DRAW ORDER and replaces
    // slots inside a clipping region with invisible tempClipContainers. The mobile background
    // spine has a 'mask' clipping slot at index 0 that wraps slots 1-11 (including _wheelSlot at
    // index 9), so after the first render this.children[9] is an invisible placeholder, not the
    // real container. slotContainers[index] is always the correct container for the slot.
    return (this as any).slotContainers?.[index] ?? this.children[index];
  };

  getBlankImageOfSlot = (slotName: string): FederatedEventTarget | null => {
    const slot: DisplayObject = this.getSlot(slotName);
    //@ts-ignore
    return slot ? (slot?.children[0] ? slot?.children[0] : slot) : null;
  };

  goToAnimationEnd = (name: string): void => {
    // const track: ITrackEntry = this.getTrackByAnimationName(name);
    // if (track) {
    //   track.trackTime = track.animationEnd;
    // };
  };

  addReversedAnimation = (sourceName: string, newName: string): void => {
    const { animations } = this.state.data.skeletonData;
    const animationData: any | IAnimation<ITimeline> = animations.find((a) => a.name === sourceName);
    if (animationData) {
      const reversedTimeLine: ITimeline[] = [...animationData.timelines];
      reversedTimeLine.reverse();
      //@ts-ignore
      animations.push(new Animation(newName, reversedTimeLine, animationData.duration));
    };
  };
};
